import CustomTable from "@/shared/components/ui/Button/CustomTable";
import PaginationInfo from "@/shared/components/ui/Pagination/PaginationInfo";
import SearchBar from "@/shared/components/ui/SearchBar/SearchBar";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useFetchSuperAdminOrganizationsList } from "./hooks/useSuperAdminApiHooks";
import { fetchVoiceOrganizations } from "./api/superadminApi";
import VoiceOrgModal, { VOICE_ORGS_QUERY_KEY } from "./VoiceOrgModal";
import {
  formatDate,
  getTextInCapitalize,
} from "@/shared/utils/helperFunctions";

const OrganizationsList = () => {
  const navigate = useNavigate();
  const [filterValue, setFilterValue] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const { data: organizationsList } = useFetchSuperAdminOrganizationsList();
  const { data: voiceOrganizations } = useQuery({
    queryKey: VOICE_ORGS_QUERY_KEY,
    queryFn: fetchVoiceOrganizations,
  });
  const voiceByOrgId = useMemo(
    () => new Map((voiceOrganizations || []).map((row) => [row.organization_id, row])),
    [voiceOrganizations],
  );
  const [voiceOrgId, setVoiceOrgId] = useState(null);

  const ORGANIZATIONS_COLUMNS = [
    {
      uid: "name",
      name: "Organization",
      sortable: true,
      align: "left",
      width: "180px",
    },
    {
      uid: "admin_email",
      name: "Admin email",
      sortable: true,
      align: "left",
      width: "200px",
    },
    {
      uid: "plan",
      name: "Plan",
      sortable: true,
      align: "left",
      width: "100px",
    },
    {
      uid: "user_count",
      name: "Members",
      sortable: true,
      align: "center",
      width: "90px",
    },
    {
      uid: "calling",
      name: "Calling",
      align: "left",
      width: "170px",
    },
    {
      uid: "created_at",
      name: "Created At",
      sortable: true,
      align: "left",
      width: "120px",
    },
    {
      uid: "updated_at",
      name: "Updated At",
      sortable: true,
      align: "center",
      width: "120px",
    },
  ];

  const filteredOrgs = useMemo(() => {
    const list = organizationsList ?? [];
    if (!filterValue) return list;
    const q = filterValue.toLowerCase();
    return list.filter(
      (org) =>
        (org.name || "").toLowerCase().includes(q) ||
        (org.admin_email || "").toLowerCase().includes(q) ||
        (org.plan || "").toLowerCase().includes(q),
    );
  }, [organizationsList, filterValue]);

  const renderCell = (org, columnKey) => {
    switch (columnKey) {
      case "name":
        return <div className="px-2">{getTextInCapitalize(org?.name)}</div>;
      case "admin_email":
        return (
          <span className="text-gray-600 text-sm">
            {org?.admin_email || "—"}
          </span>
        );
      case "plan":
        return (
          <span className="text-gray-600">
            {org?.plan ? getTextInCapitalize(String(org.plan)) : "—"}
          </span>
        );
      case "user_count":
        return <span className="text-gray-600">{org?.user_count ?? 0}</span>;
      case "calling": {
        const voice = voiceByOrgId.get(org.id);
        const status = voice?.suspended_at
          ? { label: "Suspended", tone: "text-red-600" }
          : voice?.approval_status === "pending"
            ? { label: "Approval pending", tone: "text-yellow-700" }
            : voice?.approval_status !== "approved"
              ? { label: voice?.approval_status === "rejected" ? "Declined" : "Not requested", tone: "text-gray-500" }
              : voice?.provisioning_status === "done"
                ? { label: "Active", tone: "text-green-700" }
                : { label: voice?.provisioning_status === "failed" ? "Setup failed" : "Approved, not set up", tone: "text-gray-600" };
        return (
          <div className="flex items-center gap-2">
            <div className="flex flex-col">
              <span className={`text-xs font-medium ${status.tone}`}>{status.label}</span>
              <span className="text-xs text-gray-500 tabular-nums">
                ${Number(voice?.wallet_balance_usd || 0).toFixed(2)}
              </span>
            </div>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setVoiceOrgId(org.id);
              }}
              className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Manage
            </button>
          </div>
        );
      }
      case "created_at":
        return (
          <span className="text-gray-600">{formatDate(org.created_at)}</span>
        );
      case "updated_at":
        return (
          <span className="text-gray-600">
            {org?.updated_at !== null ? formatDate(org?.updated_at) : "-"}
          </span>
        );
      default:
        return <span className="text-gray-600">{org[columnKey]}</span>;
    }
  };

  const handleRowClick = useCallback(
    (org) => {
      if (org?.id) navigate(`/sa/organizations/${org.id}`);
    },
    [navigate],
  );

  const onSearchChange = useCallback((value) => {
    setFilterValue(value);
    setPage(1);
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-[1.375rem] text-gray-800 font-bold">Organizations</h1>
      <div className="rounded-xl p-4 bg-white shadow w-full h-[calc(100dvh-90px)]">
        <div className="flex flex-col gap-4 w-full">
          <div className="flex justify-between items-center gap-4">
            <div className="flex gap-2 items-center w-full">
              <SearchBar
                value={filterValue}
                setFilterValue={setFilterValue}
                onChange={onSearchChange}
                placeholder="Search organizations..."
                className="max-w-[280px]"
              />
              <PaginationInfo
                totalCount={filteredOrgs?.length}
                currentPage={page}
                rowsPerPage={rowsPerPage}
                itemName="organization"
              />
            </div>
          </div>

          {filteredOrgs?.length > 0 ? (
            <CustomTable
              renderCell={renderCell}
              columns={ORGANIZATIONS_COLUMNS}
              data={filteredOrgs}
              totalCount={filteredOrgs?.length}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              page={page}
              onPageChange={setPage}
              height={"calc(100dvh - 178px)"}
              onRowClick={handleRowClick}
            />
          ) : (
            <div className="bg-white rounded-xl flex border h-[calc(100dvh-140px)] border-gray-200 p-12 items-center justify-center">
              <p className="text-gray-500 text-center text-xl font-bold">
                No organizations found.
              </p>
            </div>
          )}
        </div>
      </div>
      {voiceOrgId && (
        <VoiceOrgModal
          org={(organizationsList || []).find((org) => org.id === voiceOrgId)}
          voice={voiceByOrgId.get(voiceOrgId)}
          onClose={() => setVoiceOrgId(null)}
        />
      )}
    </div>
  );
};

export default OrganizationsList;
